import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import SearchIcon from '@mui/icons-material/Search';
import { Alert, Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';

import { getLaneStatusLabelKey, getSearchCategoryLabelKey } from '../../model/categories';
import { SearchCategory } from '../../model/types';
import SearchHitPreview from '../SearchHitPreview';
import SearchResultTabs from '../SearchResultTabs';
import SearchCategoryResults from './SearchCategoryResults';
import SearchGroupedResults from './SearchGroupedResults';
import SearchStartState from './SearchStartState';
import { useSearchPanelController } from './hooks';

interface Props {
  query: string;
  onQueryChange: (query: string) => void;
  onClose: () => void;
  leftOffset: number;
}

const SearchPanel: FC<Props> = ({ query, onQueryChange, onClose, leftOffset }) => {
  const { t } = useTranslation();
  const controller = useSearchPanelController({ query, onQueryChange, onClose });
  const { stream } = controller;
  const { isQueryLongEnough, isStreaming, isComplete, error, failedLanes, trimmedQuery } = stream;

  const activeEntry = controller.rows[controller.activeIndex] ?? null;
  const failedEntries = Object.entries(failedLanes) as [SearchCategory, string][];
  const hasNoResults = isQueryLongEnough && isComplete && controller.totalResults === 0;
  const isInitialStreaming = isStreaming && controller.rows.length === 0 && !isComplete;
  const isStartState = !isQueryLongEnough;

  return (
    <Paper
      elevation={0}
      data-testid="search-panel"
      data-search-overlay
      sx={{
        position: 'fixed',
        top: '1rem',
        height: isStartState ? '32dvh' : '65dvh',
        maxHeight: isStartState ? '425px' : '850px',
        minHeight: isStartState ? '275px' : '550px',
        left: `${leftOffset}px`,
        width: `min(${isStartState ? '33rem' : '66rem'}, calc(100vw - ${leftOffset}px - 1rem))`,
        zIndex: 1201,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '0.875rem',
        boxShadow: '0 1px 8px 0 rgba(0, 0, 0, 0.15)',
        overflow: 'hidden',
        '@keyframes searchPanelIn': {
          from: { opacity: 0, transform: 'translateX(-0.5rem)' },
          to: { opacity: 1, transform: 'none' },
        },
        animation: 'searchPanelIn 0.16s ease-out',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap="0.625rem"
        padding="0.875rem 4rem 0.625rem 1rem"
      >
        {isQueryLongEnough && (
          <Typography
            noWrap
            data-testid="search-results-header"
            sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.25, color: 'grey.900' }}
          >
            {t('search.results_for', { total: controller.totalResults, query: trimmedQuery })}
          </Typography>
        )}
        {isStreaming && (
          <CircularProgress size="0.875rem" data-testid="search-streaming-indicator" />
        )}
        <Box flex={1} />
      </Stack>

      {isQueryLongEnough && (
        <SearchResultTabs
          tabs={controller.tabs}
          activeTab={controller.activeTab}
          onChange={controller.changeTab}
        />
      )}

      <Stack direction="row" flex={1} minHeight={0}>
        <Stack
          flex={1}
          minWidth={0}
          data-search-scroll
          sx={{ overflowY: 'auto', padding: '0.375rem 0.5rem 0.625rem' }}
        >
          {error && <Alert severity="error">{error}</Alert>}

          {failedEntries.length > 0 && (
            <Alert
              severity="warning"
              data-testid="search-failed-lanes"
              sx={{
                alignItems: 'center',
                padding: '0.375rem 0.625rem',
                fontSize: '0.75rem',
                fontWeight: 400,
                lineHeight: 1.25,
                color: 'grey.900',
                '& .MuiAlert-icon': { padding: 0, marginRight: '0.5rem' },
                '& .MuiAlert-message': { padding: 0 },
              }}
            >
              {t('search.lanes_unavailable', {
                lanes: failedEntries
                  .map(
                    ([category, status]) =>
                      `${t(getSearchCategoryLabelKey(category))} (${t(getLaneStatusLabelKey(status))})`,
                  )
                  .join(', '),
              })}
            </Alert>
          )}

          {isStartState ? (
            <SearchStartState
              query={query}
              recentSearches={controller.recentSearches}
              onPickQuery={onQueryChange}
            />
          ) : isInitialStreaming ? (
            <Stack alignItems="center" padding="2rem">
              <CircularProgress size="1.5rem" />
            </Stack>
          ) : hasNoResults ? (
            <Stack
              alignItems="center"
              gap="0.75rem"
              padding="2.25rem 1.5rem"
              data-testid="search-no-results"
            >
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: '2.75rem',
                  height: '2.75rem',
                  borderRadius: '0.75rem',
                  backgroundColor: 'grey.50',
                  color: 'grey.400',
                }}
              >
                <SearchIcon />
              </Stack>
              <Box textAlign="center">
                <Typography
                  sx={{ fontSize: '1rem', fontWeight: 400, lineHeight: 1.25, color: 'grey.900' }}
                >
                  {t('search.no_results_for', { query: trimmedQuery })}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 400,
                    lineHeight: 1.25,
                    color: 'grey.700',
                    marginTop: '0.25rem',
                  }}
                >
                  {t('search.no_results')}
                </Typography>
              </Box>
            </Stack>
          ) : controller.activeTab === 'ALL' ? (
            <SearchGroupedResults
              groups={controller.groups}
              query={trimmedQuery}
              activeIndex={controller.activeIndex}
              onOpen={controller.openEntry}
              onActivate={controller.setActiveIndex}
              onViewAll={controller.changeTab}
            />
          ) : (
            <SearchCategoryResults
              category={controller.activeTab}
              entries={controller.rows}
              query={trimmedQuery}
              activeIndex={controller.activeIndex}
              isLoading={controller.categoryQuery.isLoading}
              totalElements={controller.totalElements}
              hasNextPage={Boolean(controller.categoryQuery.hasNextPage)}
              isFetchingNextPage={controller.categoryQuery.isFetchingNextPage}
              onLoadMore={controller.categoryQuery.fetchNextPage}
              onOpen={controller.openEntry}
              onActivate={controller.setActiveIndex}
            />
          )}
        </Stack>

        {!isStartState && !hasNoResults && (
          <Stack
            sx={{
              width: '25rem',
              flexShrink: 0,
              display: { xs: 'none', md: 'flex' },
              borderLeft: '1px solid',
              borderColor: 'grey.100',
              backgroundColor: 'grey.50',
              padding: '0.875rem',
              overflowY: 'auto',
            }}
          >
            <SearchHitPreview entry={activeEntry} onOpen={controller.openEntry} />
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default SearchPanel;
